import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { courseService } from '../../services/firebase/courseService';
import { emailService } from '../../services/email/emailService';
import './Courses.css';

const CoursesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [typeFilter, setTypeFilter] = useState('all');
  const [courses, setCourses] = useState([]);
  const [pastCourses, setPastCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      const [upcomingCourses, past] = await Promise.all([
        courseService.getUpcoming(50),
        courseService.getPast(20)
      ]);
      setCourses(upcomingCourses);
      setPastCourses(past);
    } catch (err) {
      console.error('Error loading courses:', err);
      setError('Unable to load courses. Please try again later.');
      setCourses([]);
      setPastCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString('en-AU', options);
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatSessionDates = (sessions) => {
    if (!sessions || sessions.length === 0) return 'Dates TBA';
    if (sessions.length === 1) {
      return formatShortDate(sessions[0].date);
    }
    const first = sessions[0].date;
    const last = sessions[sessions.length - 1].date;
    const firstDate = first instanceof Date ? first : new Date(first);
    const lastDate = last instanceof Date ? last : new Date(last);
    return `${firstDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })} - ${lastDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const getStatusBadge = (course) => {
    if (course.status === 'full' || course.spots?.available <= 0) return 'Full';
    if (course.status === 'filling-fast' || course.spots?.available <= 3) return 'Filling Fast';
    return `${course.spots?.available || 0} Spots Available`;
  };

  const getStatusClass = (course) => {
    if (course.status === 'full' || course.spots?.available <= 0) return 'course-badge--full';
    if (course.status === 'filling-fast' || course.spots?.available <= 3) return 'course-badge--filling';
    return 'course-badge--open';
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'training': return 'Training';
      case 'certification': return 'Certification';
      case 'course': return 'Course';
      default: return type;
    }
  };

  const displayCourses = activeTab === 'upcoming' ? courses : pastCourses;
  const types = ['all', ...new Set(displayCourses.map(c => c.type).filter(Boolean))];

  const filteredCourses = typeFilter === 'all'
    ? displayCourses
    : displayCourses.filter(course => course.type === typeFilter);

  const handleCourseClick = (course) => {
    setSelectedCourse(course);
    setShowRegistration(true);
    setRegistrationSuccess(false);
    setFormData({ name: '', email: '', phone: '', notes: '' });
  };

  const handleCloseModal = () => {
    setShowRegistration(false);
    setSelectedCourse(null);
    setRegistrationSuccess(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await courseService.enroll(selectedCourse.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes
      });

      // Send confirmation emails
      try {
        await emailService.init();
        const attendee = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          notes: formData.notes
        };

        // Send confirmation to customer
        const customerResult = await emailService.sendCourseEnrollmentConfirmation(selectedCourse, attendee);
        if (!customerResult.success) {
          console.warn('Course enrollment customer email failed:', customerResult.error);
        }

        // Send notification to admin (Kirstie)
        const adminResult = await emailService.sendCourseEnrollmentNotificationToAdmin(selectedCourse, attendee);
        if (!adminResult.success) {
          console.warn('Course enrollment admin email failed:', adminResult.error);
        }
      } catch (emailErr) {
        console.error('Error sending course enrollment emails:', emailErr);
      }

      setRegistrationSuccess(true);
      loadCourses();
    } catch (err) {
      console.error('Error enrolling:', err);
      if (err.message === 'Course is full') {
        setError('Sorry, this course is now full. Please choose another course.');
        loadCourses();
      } else if (err.message === 'You are already enrolled in this course') {
        setError('You are already enrolled in this course.');
      } else {
        setError('There was an error submitting your enrollment. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnrollNow = (course, e) => {
    e.stopPropagation();
    if (course.status === 'full' || course.spots?.available <= 0) return;
    handleCourseClick(course);
  };

  const getInstructorDisplay = (course) => {
    const instructors = course.instructors;
    if (!instructors || instructors.length === 0) return null;
    const leads = instructors.filter(i => i.role === 'lead');
    if (leads.length === 0) return null;
    return leads.map(l => l.memberName).join(' & ');
  };

  const isCoursePast = (course) => {
    // Use endDate if available - course is only past when endDate has passed
    if (course.endDate) {
      const endDate = course.endDate instanceof Date ? course.endDate : new Date(course.endDate);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return endDate < now;
    }

    // Fallback to last session date for legacy courses
    if (!course.sessions?.length) return false;
    const lastSession = course.sessions[course.sessions.length - 1];
    const sessionDate = lastSession.date instanceof Date ? lastSession.date : new Date(lastSession.date);
    return sessionDate < new Date();
  };

  const getCurrentPrice = (course) => {
    return courseService.getCurrentPrice(course);
  };

  return (
    <>
      <SEO
        title="Reiki Training & Certification Courses Sydney | KO Holistic Health"
        description="Professional Reiki training, certification courses, and wellness programs in Sydney. Learn energy healing from an experienced Reiki Master Teacher. Begin your healing journey with KO Holistic Health."
        url="/courses"
        keywords="reiki training sydney, reiki certification sydney, energy healing course, reiki level 1 sydney, reiki level 2 sydney, reiki master training, wellness courses sydney, spiritual development course, holistic healing certification, learn reiki sydney"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Courses & Training', path: '/courses' }]}
      />

      <div className="courses-page">
        <section className="courses-page-hero">
          <div className="container">
            <h1>Courses & Training</h1>
            <p className="courses-page-hero__subtitle">
              Professional certification programs and transformative learning experiences to deepen your practice
            </p>
          </div>
        </section>

        <section className="section courses-page-content">
          <div className="container">
            {/* Tab Toggle */}
            <div className="courses-tabs">
              <button
                className={`courses-tab ${activeTab === 'upcoming' ? 'courses-tab--active' : ''}`}
                onClick={() => { setActiveTab('upcoming'); setTypeFilter('all'); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Upcoming Courses
                {courses.length > 0 && <span className="courses-tab__count">{courses.length}</span>}
              </button>
              <button
                className={`courses-tab ${activeTab === 'past' ? 'courses-tab--active' : ''}`}
                onClick={() => { setActiveTab('past'); setTypeFilter('all'); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Past Courses
                {pastCourses.length > 0 && <span className="courses-tab__count">{pastCourses.length}</span>}
              </button>
            </div>

            {loading ? (
              <div className="courses-loading">
                <div className="spinner"></div>
                <p>Loading courses...</p>
              </div>
            ) : error && displayCourses.length === 0 ? (
              <div className="courses-error">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p>{error}</p>
                <Button onClick={loadCourses}>Try Again</Button>
              </div>
            ) : displayCourses.length === 0 ? (
              <div className="no-courses">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
                <h2>{activeTab === 'upcoming' ? 'No Upcoming Courses' : 'No Past Courses'}</h2>
                <p>
                  {activeTab === 'upcoming'
                    ? 'Check back soon for new training programs and certification courses.'
                    : 'Past courses will appear here once they have concluded.'}
                </p>
                {activeTab === 'upcoming' && (
                  <>
                    <p>Interested in a specific training? Contact us to express your interest.</p>
                    <Button onClick={() => navigate('/contact')}>Contact Us</Button>
                  </>
                )}
              </div>
            ) : (
              <>
                {types.length > 2 && (
                  <div className="courses-filter">
                    {types.map(type => (
                      <button
                        key={type}
                        className={typeFilter === type ? 'filter-btn active' : 'filter-btn'}
                        onClick={() => setTypeFilter(type)}
                      >
                        {type === 'all' ? 'All Programs' : getTypeLabel(type)}
                      </button>
                    ))}
                  </div>
                )}

                <div className="courses-page-grid">
                  {filteredCourses.map((course) => {
                    const priceInfo = getCurrentPrice(course);
                    return (
                      <Card
                        key={course.id}
                        className={`course-page-card ${activeTab === 'past' ? 'course-page-card--past' : ''}`}
                        onClick={() => handleCourseClick(course)}
                      >
                        {/* Course Image */}
                        {course.image && (
                          <div className="course-page-card__image">
                            <img src={course.image} alt={course.title} />
                            {activeTab === 'past' && (
                              <div className="course-page-card__past-overlay">
                                <span>Completed</span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="course-page-card__body">
                          <div className="course-page-card__header">
                            {activeTab === 'upcoming' ? (
                              <span className={`course-badge ${getStatusClass(course)}`}>
                                {getStatusBadge(course)}
                              </span>
                            ) : (
                              <span className="course-badge course-badge--past">Completed</span>
                            )}
                            <span className="course-type-badge">{getTypeLabel(course.type)}</span>
                          </div>

                          {course.certification && (
                            <div className="course-cert-badge">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="8" r="7"/>
                                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
                              </svg>
                              Certified Program
                            </div>
                          )}

                          <div className="course-page-card__schedule">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"/>
                            </svg>
                            <div>
                              <p className="course-dates">
                                {course.startDate || course.endDate
                                  ? `${course.startDate ? formatShortDate(course.startDate) : 'TBA'} — ${course.endDate ? formatShortDate(course.endDate) : 'TBA'}`
                                  : formatSessionDates(course.sessions)
                                }
                              </p>
                              <p className="course-duration">
                                {course.sessions?.length || 0} session(s) · {course.totalDuration || `${course.hoursTotal || 0} hours`}
                              </p>
                            </div>
                          </div>

                          <h3 className="course-page-card__title">{course.title}</h3>
                          {course.subtitle && (
                            <p className="course-page-card__subtitle">{course.subtitle}</p>
                          )}

                          {getInstructorDisplay(course) && (
                            <p className="course-page-card__instructor">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                              </svg>
                              Led by {getInstructorDisplay(course)}
                            </p>
                          )}

                          <p className="course-page-card__location">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                            </svg>
                            {course.location}
                            {course.isOnline && ' · Online option available'}
                          </p>

                          {course.description && (
                            <p className="course-page-card__description">
                              {course.description.length > 150
                                ? `${course.description.substring(0, 150)}...`
                                : course.description}
                            </p>
                          )}

                          {course.whatYouWillLearn && course.whatYouWillLearn.length > 0 && (
                            <div className="course-highlights">
                              <h4>You'll Learn:</h4>
                              <ul>
                                {course.whatYouWillLearn.slice(0, 3).map((point, index) => (
                                  <li key={index}>{point}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="course-page-card__footer">
                            <div className="course-footer-left">
                              <div className="course-price-info">
                                {priceInfo?.isEarlyBird ? (
                                  <>
                                    <span className="course-price course-price--early">
                                      ${priceInfo.price} AUD
                                    </span>
                                    <span className="course-price-original">
                                      ${priceInfo.regularPrice}
                                    </span>
                                    <span className="course-early-badge">Early Bird</span>
                                  </>
                                ) : (
                                  <span className="course-price">
                                    {course.price === 0 ? 'Free' : `$${course.price} AUD`}
                                  </span>
                                )}
                              </div>
                              {activeTab === 'upcoming' && (
                                <span className="course-spots">
                                  {course.spots?.available}/{course.spots?.total} spots
                                </span>
                              )}
                            </div>
                            {activeTab === 'upcoming' ? (
                              <Button
                                variant={course.status === 'full' || course.spots?.available <= 0 ? 'secondary' : 'primary'}
                                disabled={course.status === 'full' || course.spots?.available <= 0}
                                onClick={(e) => handleEnrollNow(course, e)}
                              >
                                {course.status === 'full' || course.spots?.available <= 0 ? 'Fully Booked' : 'Enroll Now'}
                              </Button>
                            ) : (
                              <Button variant="secondary" onClick={(e) => { e.stopPropagation(); handleCourseClick(course); }}>
                                View Details
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {filteredCourses.length === 0 && (
                  <div className="no-courses">
                    <p>No courses found in this category.</p>
                    <Button onClick={() => setTypeFilter('all')} variant="secondary">
                      View All Courses
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Enrollment Modal */}
      {showRegistration && selectedCourse && (
        <div className="course-modal-overlay" onClick={handleCloseModal}>
          <div className="course-modal" onClick={e => e.stopPropagation()}>
            <button className="course-modal__close" onClick={handleCloseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            {registrationSuccess ? (
              <div className="course-modal__success">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <h2>Enrollment Successful!</h2>
                <p>You're enrolled in <strong>{selectedCourse.title}</strong></p>
                <div className="course-modal__summary">
                  <p><strong>Program:</strong> {getTypeLabel(selectedCourse.type)}</p>
                  <p><strong>Dates:</strong> {
                    selectedCourse.startDate || selectedCourse.endDate
                      ? `${selectedCourse.startDate ? formatDate(selectedCourse.startDate) : 'TBA'} — ${selectedCourse.endDate ? formatDate(selectedCourse.endDate) : 'TBA'}`
                      : formatSessionDates(selectedCourse.sessions)
                  }</p>
                  <p><strong>Location:</strong> {selectedCourse.location}</p>
                  {selectedCourse.sessions?.length > 0 && (
                    <p><strong>Sessions:</strong> {selectedCourse.sessions.length} session(s)</p>
                  )}
                </div>
                <p className="course-modal__notice">
                  A confirmation email has been sent with full details and next steps.
                </p>
                <Button onClick={handleCloseModal}>Close</Button>
              </div>
            ) : (
              <>
                {/* Modal Image */}
                {selectedCourse.image && (
                  <div className="course-modal__image">
                    <img src={selectedCourse.image} alt={selectedCourse.title} />
                  </div>
                )}

                <div className="course-modal__header">
                  {!isCoursePast(selectedCourse) ? (
                    <span className={`course-badge ${getStatusClass(selectedCourse)}`}>
                      {getStatusBadge(selectedCourse)}
                    </span>
                  ) : (
                    <span className="course-badge course-badge--past">Completed</span>
                  )}
                  <span className="course-type-badge" style={{ marginLeft: '0.5rem' }}>
                    {getTypeLabel(selectedCourse.type)}
                  </span>
                  <h2>{selectedCourse.title}</h2>
                  {selectedCourse.subtitle && (
                    <p className="course-modal__subtitle">{selectedCourse.subtitle}</p>
                  )}
                </div>

                {selectedCourse.certification && (
                  <div className="course-modal__certification">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="8" r="7"/>
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
                    </svg>
                    <div>
                      <strong>{selectedCourse.certification.name}</strong>
                      {selectedCourse.certification.provider && (
                        <span>Certified by {selectedCourse.certification.provider}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="course-modal__details">
                  <div className="course-modal__info">
                    <div className="course-modal__info-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <span>
                        {selectedCourse.startDate || selectedCourse.endDate
                          ? `${selectedCourse.startDate ? formatDate(selectedCourse.startDate) : 'TBA'} — ${selectedCourse.endDate ? formatDate(selectedCourse.endDate) : 'TBA'}`
                          : formatSessionDates(selectedCourse.sessions)
                        }
                      </span>
                    </div>
                    <div className="course-modal__info-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>{selectedCourse.sessions?.length || 0} sessions · {selectedCourse.totalDuration || `${selectedCourse.hoursTotal || 0} hours total`}</span>
                    </div>
                    <div className="course-modal__info-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span>{selectedCourse.location}</span>
                    </div>
                    {getInstructorDisplay(selectedCourse) && (
                      <div className="course-modal__info-item">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                        <span>Led by {getInstructorDisplay(selectedCourse)}</span>
                      </div>
                    )}
                  </div>

                  <div className="course-modal__price-box">
                    {(() => {
                      const priceInfo = getCurrentPrice(selectedCourse);
                      if (priceInfo?.isEarlyBird) {
                        return (
                          <>
                            <span className="course-modal__price course-modal__price--early">
                              ${priceInfo.price}
                            </span>
                            <span className="course-modal__price-original">
                              ${priceInfo.regularPrice}
                            </span>
                            <span className="course-modal__early-badge">Early Bird</span>
                          </>
                        );
                      }
                      return (
                        <span className="course-modal__price">
                          {selectedCourse.price === 0 ? 'Free' : `$${selectedCourse.price}`}
                        </span>
                      );
                    })()}
                    {!isCoursePast(selectedCourse) && (
                      <span className="course-modal__spots">
                        {selectedCourse.spots?.available} spots remaining
                      </span>
                    )}
                  </div>
                </div>

                {/* Sessions Schedule */}
                {selectedCourse.sessions?.length > 0 && (
                  <div className="course-modal__section course-modal__sessions">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Session Schedule
                    </h4>
                    <div className="course-modal__sessions-list">
                      {selectedCourse.sessions.map((session, idx) => (
                        <div key={session.id || idx} className="course-modal__session-item">
                          <span className="session-date">{formatShortDate(session.date)}</span>
                          <span className="session-time">{session.startTime} - {session.endTime}</span>
                          <span className="session-title">{session.title || `Session ${idx + 1}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCourse.description && (
                  <div className="course-modal__description">
                    <p>{selectedCourse.description}</p>
                  </div>
                )}

                {/* What You'll Learn */}
                {selectedCourse.whatYouWillLearn && selectedCourse.whatYouWillLearn.length > 0 && (
                  <div className="course-modal__section">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 11 12 14 22 4"/>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                      </svg>
                      What You'll Learn
                    </h4>
                    <ul className="course-modal__benefits">
                      {selectedCourse.whatYouWillLearn.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Who Is This For */}
                {selectedCourse.whoIsThisFor && (
                  <div className="course-modal__section course-modal__section--highlight">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      Who Is This For?
                    </h4>
                    <p>{selectedCourse.whoIsThisFor}</p>
                  </div>
                )}

                {/* Requirements */}
                {selectedCourse.requirements && (
                  <div className="course-modal__section course-modal__section--highlight">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      Prerequisites
                    </h4>
                    <p>{selectedCourse.requirements}</p>
                  </div>
                )}

                {/* What to Bring */}
                {selectedCourse.whatToBring && (
                  <div className="course-modal__section course-modal__section--highlight">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      What to Bring
                    </h4>
                    <p>{selectedCourse.whatToBring}</p>
                  </div>
                )}

                {/* Materials Included */}
                {selectedCourse.materialsIncluded && (
                  <div className="course-modal__section">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                      Included Materials
                    </h4>
                    <p>{selectedCourse.materialsIncluded}</p>
                  </div>
                )}

                {/* Enrollment Form - Only for upcoming courses */}
                {!isCoursePast(selectedCourse) && selectedCourse.spots?.available > 0 && (
                  <form onSubmit={handleSubmit} className="course-modal__form">
                    <h3>Enroll in This Course</h3>

                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="0400 000 000"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="notes">Additional Notes (Optional)</label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Any questions, dietary requirements, or accessibility needs..."
                      ></textarea>
                    </div>

                    {error && (
                      <div className="form-error">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="large"
                      fullWidth
                      disabled={isSubmitting || selectedCourse.spots?.available <= 0}
                    >
                      {isSubmitting ? 'Submitting...' : 'Confirm Enrollment'}
                    </Button>
                  </form>
                )}

                {/* Past course notice */}
                {isCoursePast(selectedCourse) && (
                  <div className="course-modal__past-notice">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <p>This course has already concluded. Check our upcoming courses for similar programs.</p>
                    <Button variant="secondary" onClick={() => { handleCloseModal(); setActiveTab('upcoming'); }}>
                      View Upcoming Courses
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CoursesPage;
